import React, { memo } from "react";
import { View, StyleSheet, type LayoutChangeEvent } from "react-native";

import { MessageBubble } from "../../components/chatNewInterface/interfaceComponents/messageBubble/MessageBubble";
import { LearningActivityCard } from "../../components/chatNewInterface/interfaceComponents/learningACtivityCard/LearningActivityCard";
import { GeneratedImageCard } from "../../components/chatNewInterface/interfaceComponents/generatedImageCard/GeneratedImageCard";
import type { AttachmentChipProps } from "../../components/chatNewInterface/interfaceComponents/attachmentChip/AttachmentChip";
import type { ReadAloudControl } from "../../components/chatNewInterface/interfaceComponents/messageBubble/ReadAloudPlaybackControl";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import {
  DeepResearchProgress,
  DeepResearchResult,
  type DeepResearchProgressProps,
  type DeepResearchResultProps,
} from "../../components/companion/DeepResearch";
import { spacing, textColorStyles } from "../../styles/design-system";
import { ImageBlockStatus } from "@axio/graphql/generated/graphql";
import type { MessageItem } from "./companionMessage.types";

export const mapMessageToBubble = (message: Partial<MessageItem>) => ({
  role: (message?.sender === "human" ? "user" : "assistant") as "user" | "assistant",
  content: message?.content ?? "",
  timestamp: message?.created ? new Date(message.created) : new Date(),
  isArchived: false,
  selectedBranchId: message?.selectedBranchId,
  branches: message?.branches,
});

export const getImageGenerationState = (message: MessageItem) => {
  const imageBlock = message.imageBlocks?.[0];
  if (!imageBlock) return null;

  if (imageBlock.status === ImageBlockStatus.Completed && imageBlock.url) {
    return {
      status: "completed" as const,
      imageUrl: imageBlock.url,
      errorMessage: null,
    };
  }
  if (imageBlock.status === ImageBlockStatus.Completed && !imageBlock.url) {
    return {
      status: "failed" as const,
      imageUrl: null,
      errorMessage: "Image generation completed without an image URL.",
    };
  }
  if (imageBlock.status === ImageBlockStatus.Failed) {
    return {
      status: "failed" as const,
      imageUrl: null,
      errorMessage: imageBlock.errorMessage ?? "Image generation failed, please try again later.",
    };
  }
  return {
    status: "loading" as const,
    imageUrl: null,
    errorMessage: null,
  };
};

export interface MessageRowProps {
  item: MessageItem;
  messageIndex: number;
  isStreaming: boolean;
  currentStreamingMessageId?: string | null;
  /** True when this row is the last assistant message (status / ILE / loader attach here). */
  isLastAssistant: boolean;
  /** True when this row is the last human message (drives scroll-offset measurement). */
  isLastHuman: boolean;

  // Editing — resolved per row by the parent so unedited rows receive stable props.
  isBeingEdited: boolean;
  editValue?: string;
  onStartEdit?: (messageId: string, messageIndex: number, text: string) => void;
  onCancelEdit?: () => void;
  onChangeEditingMessageDraft?: (value: string) => void;
  onSaveEdit?: () => void;

  onSwitchBranch?: (anchorMessageId: string, targetBranchId: string | null) => void;

  // Feedback — resolved value for this row (stable unless this message's feedback changes).
  feedback?: "up" | "down";
  onFeedback?: (messageId: string, value: "up" | "down" | null) => void;

  // Read-aloud
  getAssistantReadAloudControl?: (
    messageId: string,
    content: string,
    meters?: { streamAudioLevel: number; streamAudioLevels: number[] }
  ) => ReadAloudControl | undefined;
  streamAudioLevel: number;
  streamAudioLevels: number[];

  // Status / ILE — gated to the last assistant row by the parent (null/false otherwise).
  status?: string | null;
  showIleCreationLoader?: boolean;
  ileCreationErrorMessage?: string | null;
  onOpenLearningActivity?: (ileId: string) => void;

  // Deep research — gated to the relevant rows by the parent (undefined otherwise).
  deepResearchResult?: DeepResearchResultProps;
  deepResearchProgress?: DeepResearchProgressProps;
  onCancelDeepResearch?: () => void;

  onLastHumanLayout: (offsetY: number) => void;
}

const MessageRowComponent = ({
  item,
  messageIndex,
  isStreaming,
  currentStreamingMessageId,
  isLastAssistant,
  isLastHuman,
  isBeingEdited,
  editValue,
  onStartEdit,
  onCancelEdit,
  onChangeEditingMessageDraft,
  onSaveEdit,
  onSwitchBranch,
  feedback,
  onFeedback,
  getAssistantReadAloudControl,
  streamAudioLevel,
  streamAudioLevels,
  status,
  showIleCreationLoader = false,
  ileCreationErrorMessage = null,
  onOpenLearningActivity,
  deepResearchResult,
  deepResearchProgress,
  onCancelDeepResearch,
  onLastHumanLayout,
}: MessageRowProps) => {
  const isAssistant = item.sender === "ai";

  const attachmentItems: AttachmentChipProps[] = [];
  for (const todo of item.todos ?? []) {
    attachmentItems.push({ type: "task", name: todo.name, duedate: todo.date });
  }
  for (const assignment of item.assignments ?? []) {
    attachmentItems.push({ type: "assignment", name: assignment.name, duedate: assignment.due_date });
  }
  for (const file of item.files ?? []) {
    attachmentItems.push({ type: "file", name: file.fileName });
  }

  const isStreamingCurrentMessage = isAssistant && item.id === currentStreamingMessageId && isStreaming;
  const readAloudControl =
    isAssistant && item.id && getAssistantReadAloudControl
      ? getAssistantReadAloudControl(item.id, item.content ?? "", { streamAudioLevel, streamAudioLevels })
      : undefined;

  const resolvedCreatedILE = item.createdILE ?? item.metadata?.createdILE;
  const showCreatedILE = isAssistant && resolvedCreatedILE;
  const showStatusInBubble = !!status && !ileCreationErrorMessage;
  const showIleCardLoading = showIleCreationLoader && !resolvedCreatedILE && !ileCreationErrorMessage;
  const showIleCreationError = !!ileCreationErrorMessage;
  const imageGeneration = isAssistant ? getImageGenerationState(item) : null;
  const deepResearchForThisMessage = item.deepResearch ?? deepResearchResult;

  const canEdit = !!onStartEdit && !isStreaming && item.sender === "human" && !!item.id;

  const handleLayout = (event: LayoutChangeEvent) => {
    if (isLastHuman) {
      onLastHumanLayout(event.nativeEvent.layout.y);
    }
  };

  const betweenBubbleAndActions =
    showStatusInBubble || showCreatedILE || showIleCardLoading || showIleCreationError || imageGeneration ? (
      <View style={styles.betweenBubbleContent}>
        {showStatusInBubble ? (
          <CustomText useTranslationText={false} text={status ?? ""} size="s" style={styles.statusMessageText} />
        ) : null}
        {showIleCreationError ? (
          <CustomText
            useTranslationText={false}
            text={ileCreationErrorMessage ?? ""}
            size="s"
            style={styles.ileCreationErrorText}
          />
        ) : null}
        {showCreatedILE && resolvedCreatedILE ? (
          <View style={styles.ileCardRow}>
            <LearningActivityCard
              topic={resolvedCreatedILE.title}
              description={resolvedCreatedILE.description ?? undefined}
              goals={resolvedCreatedILE.objectives?.map((objective) => objective?.objective ?? "") ?? []}
              onPress={() => (resolvedCreatedILE.id ? onOpenLearningActivity?.(resolvedCreatedILE.id) : undefined)}
              isGeneratingActivity={false}
            />
          </View>
        ) : null}
        {showIleCardLoading ? (
          <View style={styles.ileCardRow}>
            <LearningActivityCard
              topic="Creating your learning activity..."
              description={undefined}
              goals={[]}
              onPress={() => {}}
              isGeneratingActivity={true}
            />
          </View>
        ) : null}
        {imageGeneration ? (
          <GeneratedImageCard
            status={imageGeneration.status}
            imageUrl={imageGeneration.imageUrl}
            errorMessage={imageGeneration.errorMessage}
          />
        ) : null}
      </View>
    ) : undefined;

  return (
    <View onLayout={handleLayout}>
      {isAssistant && deepResearchForThisMessage && (
        <View>
          <CustomSpacing type="vertical" size="s" />
          <DeepResearchResult {...deepResearchForThisMessage} />
        </View>
      )}
      <MessageBubble
        {...mapMessageToBubble(item)}
        taskAttachmentChips={attachmentItems}
        onEdit={canEdit ? () => onStartEdit!(item.id!, item.index ?? messageIndex, item.content ?? "") : undefined}
        isBeingEdited={isBeingEdited}
        onCancelEdit={onCancelEdit}
        editValue={editValue}
        onChangeEditValue={isBeingEdited ? onChangeEditingMessageDraft : undefined}
        onSaveEdit={isBeingEdited ? onSaveEdit : undefined}
        onSwitchBranch={onSwitchBranch}
        branches={item.branches}
        selectedBranchId={item.selectedBranchId}
        feedback={feedback}
        onFeedback={isAssistant && item.id && onFeedback ? (value) => onFeedback(item.id, value) : undefined}
        hideReadAloudControl={isStreamingCurrentMessage}
        readAloudControl={readAloudControl}
        betweenBubbleAndActions={betweenBubbleAndActions}
      />
      {deepResearchProgress && (
        <View>
          <CustomSpacing type="vertical" size="s" />
          <DeepResearchProgress {...deepResearchProgress} onCancel={onCancelDeepResearch} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  betweenBubbleContent: {
    marginTop: spacing.xs,
    width: "100%",
  },
  ileCardRow: {
    marginTop: spacing.sm,
    marginLeft: 40,
    width: "100%",
  },
  statusMessageText: {
    fontStyle: "italic",
    opacity: 0.8,
  },
  ileCreationErrorText: {
    ...textColorStyles.textErrorDark,
    fontStyle: "italic",
    marginTop: spacing.xs,
  },
});

/**
 * Memoized so that, during streaming, only the row whose `item` reference actually
 * changed (the streaming message) re-renders. Non-streaming rows keep their `item`
 * identity across token updates and receive referentially-stable props (volatile
 * status/ILE/deep-research values are gated to the relevant rows by the parent), so
 * React.memo's shallow comparison skips re-rendering them — and skips re-parsing their
 * markdown. This removes the O(N)-per-token render cost behind the companion's
 * "gets slower the longer the chat gets" streaming lag (ENG-1525).
 */
export const MessageRow = memo(MessageRowComponent);

export default MessageRow;
